/**
 * Enriches the Product JSON-LD structured data with individual reviews
 * fetched from the Yotpo API. Finds the existing schema script tag,
 * fetches the 3 most recent reviews, and injects them as schema.org
 * Review objects alongside the existing aggregateRating.
 */

const YOTPO_STORE_KEY = '0fAhDCSLfKiSUCTvZm1wucV1WrUtO7XzPPD1XX3O';
const REVIEWS_TO_FETCH = 3;

/**
 * @param {string} dateString - ISO date string from Yotpo API
 * @returns {string|null} Date in YYYY-MM-DD format, or null if invalid
 */
const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
};

/**
 * @typedef {Object} YotpoReview
 * @property {number} score
 * @property {string} content
 * @property {string} created_at
 * @property {Object} user
 * @property {string} user.display_name
 */

/**
 * Checks whether a Yotpo review has the minimum data required for a valid schema.org Review.
 * @param {YotpoReview} review
 * @returns {boolean}
 */
const isValidReview = (review) => {
  if (!review) return false;
  if (!review.content) return false;
  if (!review.user?.display_name) return false;
  if (!review.score) return false;
  return true;
};

/**
 * Maps a Yotpo review object to a schema.org Review structure.
 * @param {YotpoReview} review - Pre-validated review with required fields
 * @returns {Object} schema.org Review object
 */
const mapReviewToSchema = (review) => {
  const schema = {
    '@type': 'Review',
    'author': {
      '@type': 'Person',
      'name': review.user.display_name,
    },
    'description': review.content,
    'reviewRating': {
      '@type': 'Rating',
      'bestRating': '5',
      'ratingValue': String(review.score),
      'worstRating': '1',
    },
    'reviewBody': review.content,
  };

  const date = formatDate(review.created_at);
  if (date) {
    schema.datePublished = date;
  }

  return schema;
};

/**
 * Fetches reviews for a product from the Yotpo API.
 * @param {string} productId - Shopify product ID
 * @returns {Promise<YotpoReview[]|null>}
 */
const fetchYotpoReviews = async (productId) => {
  const url = `https://api-cdn.yotpo.com/v1/widget/${YOTPO_STORE_KEY}/products/${productId}/reviews.json?per_page=${REVIEWS_TO_FETCH}&sort=date&direction=desc`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data?.response?.reviews ?? null;
  } catch {
    return null;
  }
};

const enrichProductSchema = async () => {
  const scriptTag = document.querySelector('script[type="application/ld+json"][data-product-id]');
  if (!scriptTag) return;

  const productId = scriptTag.dataset.productId;
  if (!productId) return;

  const reviews = await fetchYotpoReviews(productId);
  if (!reviews || reviews.length === 0) return;

  const validReviews = reviews.filter(isValidReview);
  if (validReviews.length === 0) return;

  try {
    const schema = JSON.parse(scriptTag.textContent);
    schema.review = validReviews.map(mapReviewToSchema);
    scriptTag.textContent = JSON.stringify(schema);
  } catch {
    // Schema remains unchanged if parsing fails
  }
};

enrichProductSchema();
